import { useEffect, useRef } from 'react';
import { languageName } from '../../shared/languages';
import { useT } from '../lib/i18n';

interface WaiterQuestionOverlayProps {
  open: boolean;
  question: string;
  questionLang: string;
  sourceLanguage: string;
  translated: boolean;
  severity: 'preference' | 'strict';
  englishGloss: string;
  onClose: () => void;
}

// Fullscreen black overlay with the question in very large source-language text
// for the waiter to read at arm's length. The decorative thumbs up/down icons
// signal "this is a question expecting an answer," not interactive.
export function WaiterQuestionOverlay({
  open,
  question,
  questionLang,
  sourceLanguage,
  translated,
  severity,
  englishGloss,
  onClose,
}: WaiterQuestionOverlayProps) {
  const t = useT();
  const closeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    queueMicrotask(() => closeRef.current?.focus());
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const accent = severity === 'strict' ? '#FF4444' : '#FFC944';

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Question for the waiter"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 70,
        background: '#000000',
        color: '#FFFFFF',
        display: 'flex',
        flexDirection: 'column',
        padding: 'max(28px, env(safe-area-inset-top)) 24px max(28px, env(safe-area-inset-bottom))',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span
          style={{
            fontSize: 11,
            letterSpacing: '0.1em',
            textTransform: 'uppercase',
            color: accent,
            fontWeight: 600,
          }}
        >
          {severity === 'strict' ? 'Allergy / Medical' : 'Question'}
        </span>
        <button
          ref={closeRef}
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
          aria-label={t('order.dismiss')}
          style={{
            width: 44,
            height: 44,
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.3)',
            background: 'transparent',
            color: '#FFFFFF',
            fontSize: 22,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ×
        </button>
      </div>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
        <p
          lang={questionLang}
          style={{
            fontSize: 32,
            lineHeight: 1.3,
            fontWeight: 500,
            margin: 0,
            letterSpacing: '-0.01em',
            textAlign: 'center',
          }}
        >
          {question}
        </p>
        {!translated && (
          <p
            style={{
              fontSize: 13,
              color: accent,
              textAlign: 'center',
              margin: '16px 0 0',
              fontWeight: 600,
              letterSpacing: '0.02em',
            }}
          >
            Shown in English — {languageName(sourceLanguage)} translation coming soon.
          </p>
        )}
        {translated && (
          <p
            style={{
              fontSize: 13,
              color: 'rgba(255,255,255,0.55)',
              textAlign: 'center',
              margin: '20px 0 0',
              fontStyle: 'italic',
            }}
          >
            {englishGloss}
          </p>
        )}
      </div>

      <div aria-hidden style={{ display: 'flex', justifyContent: 'center', gap: 36, opacity: 0.45, paddingBottom: 8 }}>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M7 10v12" />
          <path d="M15 5.88 14 10h5.83a2 2 0 0 1 1.92 2.56l-2.33 8A2 2 0 0 1 17.5 22H7" />
        </svg>
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
          <path d="M17 14V2" />
          <path d="M9 18.12 10 14H4.17a2 2 0 0 1-1.92-2.56l2.33-8A2 2 0 0 1 6.5 2H17" />
        </svg>
      </div>
    </div>
  );
}
